import theme from 'app/theme';
import Currency from 'app/shared-components/util/Currency';
import ResponsiveTable from 'app/shared-components/layout/table/ResponsiveTable';
import PaymentCardUsers from 'app/shared-components/paymentCard/PaymentCardUsers';
import ResponsiveTableHead from 'app/shared-components/layout/table/ResponsiveTableHead';
import { StyledTableRow } from 'app/pages/overview/tabs/transactions/style';
import { useTranslation } from 'react-i18next';
import { useObjectsTranslation } from 'app/hooks/useObjectsTranslation';
import { formatDateInEastern } from 'util/dateUtils';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { Box, Chip, Fade, TableCell, Tooltip, Typography } from '@mui/material';
import {
  PaidolUserToHighnotePaymentCard,
  HighnoteTransactionStatus,
  HighnoteTransactionEvent,
  HNAmount,
} from 'API';
import { groupSplitRows, type SplitAwareTransaction } from 'app/util/splitRows';
import { useMemo } from 'react';
export interface Props {
  loading: boolean;
  transactions: Array<HighnoteTransactionEvent>;
  cardUsers?: Array<PaidolUserToHighnotePaymentCard>;
}

const CardTransactionsTable = ({ transactions, cardUsers }: Props) => {
  const { t } = useTranslation();

  const TABLE_HEAD_COLUMNS = [
    {
      id: 'Card User',
      label: t('cardUser'),
      align: 'left' as const,
    },
    {
      id: 'Merchant',
      label: t('merchant'),
      align: 'left' as const,
    },
    {
      id: 'Amount',
      label: t('amount'),
      align: 'right' as const,
    },
    {
      id: 'Date',
      label: t('date'),
      align: 'left' as const,
    },
  ];

  const tableHeadColumns = useObjectsTranslation(TABLE_HEAD_COLUMNS);

  // SEE-1604: a split parent sits above its children, muted, so the list reads the way
  // the card total is computed (children count, the parent does not).
  const rows = useMemo(() => groupSplitRows(transactions as SplitAwareTransaction[]), [transactions]);

  if (transactions.length === 0) {
    return (
      <Fade in>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 2,
            p: { xs: 2 },
          }}
        >
          <Typography color="textSecondary" variant="h5">
            {t('noTransactionsForCard')}
          </Typography>
        </Box>
      </Fade>
    );
  }

  const getAmountCell = (status: HighnoteTransactionStatus, amount: HNAmount) => {
    if (!amount.currencyCode || !amount.value) return;

    return status === HighnoteTransactionStatus.CLEARED || status === HighnoteTransactionStatus.APPROVED ? (
      <Currency value={amount.value} currency={amount.currencyCode} />
    ) : (
      <Tooltip title={t('refund')} placement="top" arrow>
        <Typography fontSize={12} fontWeight="500" color={'error.main'}>
          <Currency value={amount.value} currency={amount.currencyCode} />
        </Typography>
      </Tooltip>
    );
  };

  return (
    <ResponsiveTable TableHead={<ResponsiveTableHead columns={tableHeadColumns} align="center" />}>
      {rows.map((transaction) => {
        const isComplete = transaction.status !== HighnoteTransactionStatus.APPROVED;
        const split = transaction._split;
        const isParent = split?.role === 'parent';
        const isChild = split?.role === 'child' && split.parentPresent;

        return (
          <StyledTableRow
            // Children share the parent's Highnote transactionId; the row id is unique.
            key={transaction.id}
            color={isComplete ? theme.palette.text.secondary : theme.palette.secondary.dark}
            sx={isChild ? { backgroundColor: 'grey.50' } : undefined}
            data-testid={isParent ? 'card-split-parent' : isChild ? 'card-split-child' : undefined}
          >
            <TableCell align="left" sx={{ fontWeight: '500', opacity: isChild ? 0.45 : 1 }}>
              <PaymentCardUsers users={cardUsers} />
            </TableCell>
            <TableCell align="left" sx={{ fontWeight: '500' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: isChild ? 3 : 0 }}>
                <Box component="span" sx={{ opacity: isChild ? 0.45 : 1 }}>
                  {transaction.merchantName}
                </Box>
                {isParent && (
                  <Chip
                    size="small"
                    icon={<CallSplitIcon sx={{ fontSize: 14 }} />}
                    label={t('splitRows.parentPillCount', 'Split {{n}}', { n: split.count })}
                    sx={{ height: 20, fontSize: '1.1rem', fontWeight: 600 }}
                  />
                )}
                {isChild && (
                  <Box component="span">
                    {t('splitRows.childMarkerShort', '- {{n}} of {{m}}', { n: split.index, m: split.count })}
                  </Box>
                )}
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: '500', opacity: isParent ? 0.45 : 1 }}>
              {getAmountCell(transaction.status, transaction.approvedAmount)}
            </TableCell>
            <TableCell align="left" sx={{ fontWeight: '500', opacity: isChild ? 0.45 : 1 }}>
              {isComplete ? formatDateInEastern(transaction.eventDate) : t('pending')}
            </TableCell>
          </StyledTableRow>
        );
      })}
    </ResponsiveTable>
  );
};
export default CardTransactionsTable;
